import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { ForgotPasswordDialog } from "~/components/auth/ForgotPasswordDialog";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { useToast } from "~/components/ui/use-toast";
import { supabase } from "~/integrations/supabase/client";

// Define validation schema for login form.
// Trim/lowercase the email and trim the password so a stray leading/trailing
// space (commonly added by mobile keyboard autocomplete) can't cause a false
// "Invalid login credentials" — Supabase lowercases emails server-side but does
// not trim whitespace on either field.
const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email("Ingresa un correo electrónico válido"),
  password: z
    .string()
    .trim()
    .min(6, "La contraseña debe tener al menos 6 caracteres"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const Login = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);

  useEffect(() => {
    // Check if the user is already authenticated
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        // Check the user's role to determine where to redirect
        const { data: profileData } = await supabase
          .from("profiles")
          .select("role")
          .eq("id", data.session.user.id)
          .single();

        // If role is seller, redirect to sales-notes
        if (profileData?.role === "seller") {
          navigate("/sales-notes");
        } else {
          navigate("/");
        }
      }
    };

    checkAuth();
  }, [navigate]);

  // Set up the login form
  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Handle login form submission
  const onLoginSubmit = async (values: LoginFormValues) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });

      if (error) {
        toast({
          title: "Error de inicio de sesión",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      if (data.user) {
        // Check user role and activation status
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("role, is_active, deactivated_at")
          .eq("id", data.user.id)
          .single();

        if (profileError || !profileData) {
          toast({
            title: "Error",
            description: "No se pudo verificar el perfil del usuario.",
            variant: "destructive",
          });
          await supabase.auth.signOut();
          return;
        }

        // Check if user is deactivated
        if (!profileData.is_active) {
          toast({
            title: "Cuenta desactivada",
            description:
              "Su cuenta ha sido desactivada. Contacte al administrador.",
            variant: "destructive",
          });
          await supabase.auth.signOut();
          return;
        }

        // Redirect based on role
        if (profileData?.role === "seller") {
          navigate("/sales-notes");
        } else {
          navigate("/");
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      toast({
        title: "Error de inicio de sesión",
        description: "Hubo un problema al iniciar sesión. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center">
          <img
            src="/aliace-logo.jpg"
            alt="Logo"
            className="h-20 w-20 object-contain"
          />
          <h1 className="mt-4 text-2xl font-bold">ALIACE</h1>
          <p className="text-center text-gray-500">
            Sistema de Gestión Comercial
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <Form {...loginForm}>
              <form
                onSubmit={loginForm.handleSubmit(onLoginSubmit)}
                className="space-y-6"
              >
                <FormField
                  control={loginForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Correo Electrónico</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="correo@ejemplo.com"
                          type="email"
                          inputMode="email"
                          autoCapitalize="none"
                          autoCorrect="off"
                          autoComplete="email"
                          spellCheck={false}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={loginForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contraseña</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          autoComplete="current-password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-4">
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Iniciando sesión..." : "Iniciar Sesión"}
                  </Button>

                  <div className="text-center">
                    <Button
                      type="button"
                      variant="link"
                      className="text-sm text-gray-600 hover:text-gray-800"
                      onClick={() => setIsForgotPasswordOpen(true)}
                    >
                      ¿Olvidaste tu contraseña?
                    </Button>
                  </div>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>

      <ForgotPasswordDialog
        isOpen={isForgotPasswordOpen}
        onOpenChange={setIsForgotPasswordOpen}
      />
    </div>
  );
};

export default Login;
